import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { api, errMsg, money, Listing, Kind, Booking } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Consent, StatusBadge } from "@/components/site/Bits";
import { DateRange } from "@/components/site/DateRange";
import { InquiryForm } from "@/components/site/InquiryForm";

interface Quote { units: number; unit_label: string; pricing: Booking["pricing"] }

const Confirmation = ({ b }: { b: Booking }) => (
  <div className="space-y-4" data-testid="booking-confirmation">
    <CheckCircle2 className="text-emerald-600" size={32} />
    <h3 className="text-2xl">Request received</h3>
    <p className="text-sm text-navy/65">This is a request, not a confirmed booking. Our team will review availability and contact you. No payment has been taken.</p>
    <div className="rounded-xl bg-stone-canvas p-4">
      <p className="label">Your reference</p>
      <p className="font-mono text-2xl font-bold tracking-wider" data-testid="booking-reference">{b.reference}</p>
      <div className="mt-2"><StatusBadge status={b.status} testid="booking-confirmation-status" /></div>
    </div>
    <p className="text-xs text-navy/55">Save this reference. Track status anytime with your reference and email in My Bookings.</p>
    <Link to={`/my-bookings?ref=${b.reference}`} className="btn-navy w-full" data-testid="booking-track-link">Track my request</Link>
  </div>
);

const RequestRate = ({ item, kind }: { item: Listing; kind: Kind }) => (
  <div className="card-lux space-y-4 p-6 sm:p-7" data-testid="rate-request-widget">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brass-deep">Rate on request</p>
    <h3 className="text-xl">Request availability</h3>
    <p className="text-sm text-navy/60">Tell us your dates and pick-up location. Our team confirms the exact vehicle, availability and rate with you before anything is booked.</p>
    <InquiryForm kind={kind === "vehicle" ? "vehicle" : "general"} subjectId={item.id} subjectTitle={item.title} extras={["date", "pickup"]} cta="Request availability" testid="rate-request" successTitle="Request received" />
  </div>
);

export const BookingWidget = (props: { item: Listing; kind: Kind; initial?: { start?: string; end?: string; guests?: number } }) =>
  props.item.rate ? <PricedWidget {...props} /> : <RequestRate item={props.item} kind={props.kind} />;

const PricedWidget = ({ item, kind, initial }: { item: Listing; kind: Kind; initial?: { start?: string; end?: string; guests?: number } }) => {
  const { user } = useAuth();
  const [start, setStart] = useState(initial?.start || "");
  const [end, setEnd] = useState(initial?.end || "");
  const [guests, setGuests] = useState(initial?.guests || 1);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [qErr, setQErr] = useState("");
  const [f, setF] = useState({ name: "", email: "", phone: "", country: "", notes: "", pickup_location: "", driver_age: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Booking | null>(null);
  const unit = kind === "stay" ? "night" : "day";

  useEffect(() => {
    if (user) setF((p) => ({ ...p, name: p.name || user.name, email: p.email || user.email, phone: p.phone || user.phone || "" }));
  }, [user]);

  useEffect(() => {
    setQuote(null);
    setQErr("");
    if (!start || !end) return;
    const t = setTimeout(() => {
      api.post("/bookings/quote", { kind, item_id: item.id, start_date: start, end_date: end, guests })
        .then((r) => setQuote(r.data)).catch((e) => setQErr(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [start, end, guests, kind, item.id]);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the terms and policies");
    setBusy(true);
    try {
      const r = await api.post("/bookings", { kind, item_id: item.id, start_date: start, end_date: end, guests, ...f,
        driver_age: f.driver_age ? Number(f.driver_age) : null, accept_terms: agree });
      setDone(r.data);
      toast.success(`Request ${r.data.reference} submitted`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <div className="card-lux p-6 sm:p-8"><Confirmation b={done} /></div>;

  return (
    <form onSubmit={submit} className="card-lux space-y-5 p-6 shadow-[0_24px_60px_-30px_rgba(13,27,42,0.35)] sm:p-7" data-testid="booking-widget">
      <div className="flex items-baseline justify-between">
        <p><span className="font-mono text-2xl font-bold">{money(item.rate || 0, item.currency)}</span> <span className="text-sm text-navy/55">/ {unit}</span></p>
        <span className="text-xs text-navy/50">Min {item.min_units || 1} {unit}s</span>
      </div>
      {(item.weekly_rate || item.monthly_rate) ? (
        <p className="-mt-2 text-xs text-navy/55" data-testid="booking-long-rates">{[item.weekly_rate && `${money(item.weekly_rate, item.currency)} / week (7+ ${unit}s)`, item.monthly_rate && `${money(item.monthly_rate, item.currency)} / month (28+ ${unit}s)`].filter(Boolean).join(" · ")}</p>
      ) : null}
      <div className="overflow-hidden rounded-xl border border-navy/15">
        <DateRange testid="booking-dates" start={start} end={end} labels={kind === "stay" ? ["Check-in", "Check-out"] : ["Pick-up", "Return"]}
          unavailable={item.unavailable} onChange={(s, e) => { setStart(s); setEnd(e); }} />
      </div>
      <label className="block">
        <span className="label">{kind === "stay" ? "Guests" : "Passengers"}</span>
        <input type="number" min={1} max={item.capacity || 20} value={guests} onChange={(e) => setGuests(Math.max(1, Number(e.target.value)))} className="field" data-testid="booking-guests" />
      </label>
      {qErr && <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700" data-testid="booking-quote-error">{qErr}</p>}
      {quote && (
        <div className="space-y-2 rounded-xl bg-stone-canvas p-4 text-sm" data-testid="booking-quote">
          <div className="flex justify-between"><span>{money(quote.pricing.rate, quote.pricing.currency)} × {quote.units} {quote.unit_label}s{(quote.pricing as any).rate_basis === "weekly" ? " (weekly rate)" : (quote.pricing as any).rate_basis === "monthly" ? " (monthly rate)" : ""}</span><span className="font-mono">{money(quote.pricing.subtotal, quote.pricing.currency)}</span></div>
          {quote.pricing.fee > 0 && <div className="flex justify-between"><span>{quote.pricing.fee_label}</span><span className="font-mono">{money(quote.pricing.fee, quote.pricing.currency)}</span></div>}
          <div className="flex justify-between border-t border-navy/10 pt-2 font-semibold"><span>Estimated total</span><span className="font-mono" data-testid="booking-quote-total">{money(quote.pricing.total, quote.pricing.currency)}</span></div>
          {quote.pricing.security_deposit > 0 && <p className="text-xs text-navy/55" data-testid="booking-deposit-note">Refundable security deposit of {money(quote.pricing.security_deposit, quote.pricing.currency)} {kind === "vehicle" ? "is required before pickup (not included in the total)." : "may apply."}</p>}
        </div>
      )}
      {quote && (
        <div className="space-y-3 fade-up">
          <input required placeholder="Full name" value={f.name} onChange={set("name")} className="field" data-testid="booking-name" />
          <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className="field" data-testid="booking-email" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Phone / WhatsApp" value={f.phone} onChange={set("phone")} className="field" data-testid="booking-phone" />
            <input placeholder="Country" value={f.country} onChange={set("country")} className="field" data-testid="booking-country" />
          </div>
          {kind === "vehicle" && (
            <div className="grid grid-cols-2 gap-3">
              <input required type="number" min={16} placeholder={`Driver age (${item.min_driver_age || 21}+)`} value={f.driver_age} onChange={set("driver_age")} className="field" data-testid="booking-driver-age" />
              <input placeholder="Pick-up location" value={f.pickup_location} onChange={set("pickup_location")} className="field" data-testid="booking-pickup" />
            </div>
          )}
          <textarea placeholder="Anything we should know? (optional)" rows={3} value={f.notes} onChange={set("notes")} className="field" data-testid="booking-notes" />
          <Consent testid="booking-terms-checkbox" checked={agree} onChange={setAgree}
            text={<>I agree to the <Link to="/policies/terms" className="underline">Terms</Link>, <Link to={kind === "stay" ? "/policies/accommodation" : "/policies/vehicle-rental"} className="underline">{kind === "stay" ? "Accommodation" : "Vehicle Rental"} Terms</Link>, <Link to="/policies/cancellation" className="underline">Cancellation</Link> and <Link to="/policies/privacy" className="underline">Privacy</Link> policies.</>} />
        </div>
      )}
      <button disabled={!quote || busy} className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-50" data-testid="booking-submit-button">
        {busy ? <Loader2 className="animate-spin" size={16} /> : null} {quote ? "Send booking request" : "Select dates to see price"}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-navy/50"><ShieldCheck size={13} /> Approval-first. You will not be charged now.</p>
    </form>
  );
};
